import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import { enumRespErrors, enumSimilarHtsSuggestionScope } from '@data-layer/enums/common';
import { enumUserBetaFlag } from '@data-layer/enums/user';
import { SimilarHtsConfirmModel, SimilarHtsSuggestionModel } from '@data-layer/models/common';
import { FavoriteHtsNumberModel } from '@data-layer/models/common/favorite-hts-number.model';
import { CompanyModel } from '@data-layer/models/companies';
import { EntrySummaryModel, LineModel } from '@data-layer/models/entry-summaries';
import { HtsChatRepository } from '@data-layer/repositories';
import { AlertService } from '@services/alert.service';
import { CompaniesService } from '@services/companies.service';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { HTS_SUGGESTIONS_MIN_LENGTH } from '../const/hts-assistant.const';

/**
 * A catalog item or a similar-product code onto a SAVED line, through the backend (#13411 / #13517): the backend writes
 * the code and the verification — never the description — and may verify sibling lines, so the entry is re-read after.
 * Tagged companies only; the hosts keep the form-filling path for everyone else. Refusals go to the global alert.
 */
@Injectable({ providedIn: 'root' })
export class HtsLineInsertService {
  constructor(
    private htsChatRepository: HtsChatRepository,
    private entrySummaryStateService: EntrySummaryStateService,
    private companiesService: CompaniesService,
    private alertService: AlertService,
  ) {}

  public get isEnabled(): boolean {
    return this.companiesService.hasUserBetaFlag(enumUserBetaFlag.htsClassificationAssistant);
  }

  public insertCatalogItem(lineId: number, item: FavoriteHtsNumberModel): Observable<LineModel> {
    return this.afterInsert(this.htsChatRepository.insertCatalogItem(lineId, item.id));
  }

  public confirmSuggestion(lineId: number, suggestion: SimilarHtsSuggestionModel): Observable<LineModel> {
    const body: SimilarHtsConfirmModel = {
      htsNumber: suggestion.htsNumber,
      conversationId: suggestion.conversationId,
      sourceLineId: suggestion.sourceLineId,
    };
    return this.afterInsert(this.htsChatRepository.confirmSuggestion(lineId, body));
  }

  /**
   * Similar products for a typed text, from every source (#13534: the same as the saved line gets); the saved line being
   * edited is left out through `editedLineId`. Empty for untagged companies, short texts and failures.
   */
  public searchByText(text: string | null, editedLineId: number | null = null): Observable<SimilarHtsSuggestionModel[]> {
    const trimmed: string = (text || '').trim();
    const company: CompanyModel | null = this.companiesService.getCompany();
    if (trimmed.length < HTS_SUGGESTIONS_MIN_LENGTH || !this.isEnabled || !company || !company.id) {
      return of([]);
    }
    return this.htsChatRepository.searchCompanySuggestions(company.id, trimmed, enumSimilarHtsSuggestionScope.All, editedLineId).pipe(
      map((suggestions: SimilarHtsSuggestionModel[]) => suggestions || []),
      catchError(() => of([] as SimilarHtsSuggestionModel[])),
    );
  }

  private afterInsert(request$: Observable<LineModel>): Observable<LineModel> {
    return request$.pipe(
      take(1),
      switchMap((line: LineModel) => this.reloadEntry().pipe(map(() => line))),
      catchError((err: HttpErrorResponse) => {
        this.alertService.modalError(this.errorKey(err));
        return throwError(err);
      }),
    );
  }

  /** The response carries the target line only; siblings may have been verified too. A failed reload is not an error. */
  private reloadEntry(): Observable<EntrySummaryModel | null> {
    const entry: EntrySummaryModel | null = this.entrySummaryStateService.entrySummary$.getValue();
    if (!entry || !entry.referenceNumber) {
      return of(null);
    }
    return this.entrySummaryStateService.loadEntrySummary(entry.referenceNumber).pipe(
      take(1),
      tap((loaded: EntrySummaryModel) => this.entrySummaryStateService.updateCurrentEntrySummary(loaded)),
      catchError(() => of(null)),
    );
  }

  private errorKey(err: HttpErrorResponse): string {
    const tag: string | null = err && err.error ? err.error.tag : null;
    switch (tag) {
      case enumRespErrors.CatalogItemCodeWithdrawn:
      case enumRespErrors.HtsSuggestionCodeWithdrawn:
        return 'HTS_ASSISTANT.NOTICE.CODE_UNAVAILABLE';
      case enumRespErrors.HtsSuggestionNotFound:
      case enumRespErrors.HtsSuggestionNotSimilar:
        return 'HTS_ASSISTANT.NOTICE.SUGGESTION_STALE';
      case enumRespErrors.EntityIsLocked:
        return 'HTS_ASSISTANT.NOTICE.LOCKED';
      case enumRespErrors.BetaFeatureDisabled:
        return 'HTS_ASSISTANT.NOTICE.FEATURE_DISABLED';
      default:
        return 'HTS_ASSISTANT.NOTICE.TRY_AGAIN';
    }
  }
}
